import React, { useState, useMemo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import SafeIcon from '../../common/SafeIcon';
import { useCRM } from '../../context/CRMContext';
import { formatCurrency } from '../../utils/helpers';

const COACH_COLLAPSED_KEY = 'saleswork_ai_coach_collapsed';

const getInitialCollapsedState = () => {
  try {
    return localStorage.getItem(COACH_COLLAPSED_KEY) === 'true';
  } catch {
    return false;
  }
};

const AICopilotWidget = ({ deals: scopedDeals, activities: scopedActivities, pipelineId, isScopedPipeline = false, onOpenDeal }) => {
  const { deals: globalDeals, activities: globalActivities } = useCRM();
  const hasDealsProp = scopedDeals !== undefined;
  const deals = hasDealsProp || isScopedPipeline
    ? (Array.isArray(scopedDeals) ? scopedDeals : [])
    : globalDeals;
  const activities = hasDealsProp || isScopedPipeline
    ? (Array.isArray(scopedActivities) ? scopedActivities : [])
    : globalActivities;
  const [activeInsight, setActiveInsight] = useState(0);
  const [isCoachCollapsed, setIsCoachCollapsed] = useState(
    getInitialCollapsedState
  );
  const [actionMessage, setActionMessage] = useState('');

  const insights = useMemo(() => {
    const list = [];
    const now = new Date();
    const scopedDealList = isScopedPipeline
      ? deals.filter(deal => (
        !pipelineId ||
        String(deal.pipeline_id || deal.pipelineId || '') === String(pipelineId)
      ))
      : deals;

    if (isScopedPipeline && scopedDealList.length === 0) {
      return [{
        type: 'neutral',
        icon: '⭐',
        title: 'Ingen leads i denne pipelinen ennå.',
        description: 'AI-coachen analyserer leads når de blir lagt til i denne pipelinen.',
        action: 'Se pipeline',
        color: 'text-slate-600 dark:text-slate-400',
        bg: 'bg-slate-50 dark:bg-slate-900/10',
        border: 'border-slate-100 dark:border-slate-800'
      }];
    }

    const relevantActivities = isScopedPipeline
      ? activities.filter(activity => scopedDealList.some(deal => (
        String(deal.deal_id || deal.id || '') === String(activity.deal_id || activity.dealId || '')
      )))
      : activities;

    // 1. Stale Deals Analysis
    const staleDeals = scopedDealList.filter(d => {
      if (d.status !== 'Åpen') return false;
      const dealActivities = relevantActivities.filter(a => a.deal_id === d.deal_id);
      if (dealActivities.length === 0) return true;
      const lastActivity = new Date(Math.max(...dealActivities.map(a => new Date(a.date))));
      const diffDays = Math.ceil((now - lastActivity) / (1000 * 60 * 60 * 24));
      return diffDays > 5;
    });

    if (staleDeals.length > 0) {
      const topDeal = staleDeals.sort((a, b) => b.value - a.value)[0];
      list.push({
        type: 'warning',
        icon: '⚠️',
        title: 'Stale Deal: ' + topDeal.title,
        description: `Ingen aktivitet på 5+ dager. Denne avtalen verdt ${formatCurrency(topDeal.value)} står i fare for å kjølne.`,
        action: 'Send oppfølging',
        deal: topDeal,
        color: 'text-amber-600 dark:text-amber-400',
        bg: 'bg-amber-50 dark:bg-amber-900/10',
        border: 'border-amber-100 dark:border-amber-900/20'
      });
    }

    // 2. Upcoming Meetings Context
    const today = now.toISOString().split('T')[0];
    const meetingsToday = relevantActivities.filter(a => 
      (a.type === 'Møte' || a.type === 'Online møte') && 
      a.date.startsWith(today) && 
      a.status === 'Planlagt'
    );

    if (meetingsToday.length > 0) {
      list.push({
        type: 'info',
        icon: '📅',
        title: `Du har ${meetingsToday.length} møter i dag`,
        description: `Husk å se over forrige referat. Her er en kjapp oppsummering av viktigste punkter.`,
        action: 'Se brief',
        color: 'text-indigo-600 dark:text-indigo-400',
        bg: 'bg-indigo-50 dark:bg-indigo-900/10',
        border: 'border-indigo-100 dark:border-indigo-900/20'
      });
    }

    // Default if list is empty
    if (list.length === 0) {
      list.push({
        type: 'neutral',
        icon: '⭐',
        title: 'Alt ser bra ut',
        description: 'AI-coachen analyserer dataene dine i sanntid. Akkurat nå er det ingen kritiske avvik.',
        action: 'Gjør et dypdykk',
        color: 'text-slate-600 dark:text-slate-400',
        bg: 'bg-slate-50 dark:bg-slate-900/10',
        border: 'border-slate-100 dark:border-slate-800'
      });
    }

    return list;
  }, [deals, activities, isScopedPipeline, pipelineId]);

  const current = insights[activeInsight];

  const handleCollapseToggle = () => {
    setIsCoachCollapsed(value => {
      const nextValue = !value;

      try {
        localStorage.setItem(COACH_COLLAPSED_KEY, String(nextValue));
      } catch {
        // Local storage may be unavailable in private browsing.
      }

      return nextValue;
    });
  };

  const handleOpenDeal = () => {
    if (current.deal && typeof onOpenDeal === 'function') {
      const dealIsInScopedList = !isScopedPipeline || deals.some(deal => (
        String(deal.deal_id || deal.id || '') === String(current.deal.deal_id || current.deal.id || '')
      ));

      if (dealIsInScopedList) {
        onOpenDeal(current.deal);
        return;
      }
    }

    setActionMessage('Leaden finnes ikke lenger i aktiv pipeline.');
  };

  const handleFollowUp = () => {
    if (!current.deal) {
      setActionMessage('Ingen oppfølgingsklar lead funnet akkurat nå.');
      return;
    }

    const contactEmail = current.deal.email || current.deal.contactEmail;

    if (contactEmail) {
      const draft = `Emne: Oppfølging

Hei,

Jeg ville bare følge opp dialogen vår og høre om dette fortsatt er aktuelt.

Passer det med en kort prat denne uken?

Vennlig hilsen
Start Demo`;

      if (navigator.clipboard?.writeText) {
        navigator.clipboard.writeText(draft).then(
          () => setActionMessage(
            `Oppfølgingsforslag kopiert for ${current.deal.title || 'leaden'}. Åpne leadkortet og velg Send Email.`
          ),
          () => setActionMessage(
            `Oppfølgingsforslag klart for ${current.deal.title || 'leaden'}. Åpne leadkortet og velg Send Email.`
          )
        );
      } else {
        setActionMessage(
          `Oppfølgingsforslag klart for ${current.deal.title || 'leaden'}. Åpne leadkortet og velg Send Email.`
        );
      }

      return;
    }

    setActionMessage(
      'Leaden mangler e-postadresse. Legg inn e-post før oppfølging kan sendes.'
    );
  };

  return (
    <div className={`rounded-[2.5rem] border ${current.border} ${current.bg} p-8 relative overflow-hidden transition-all duration-500`}>
      {/* Background Decor */}
      <div className="absolute top-[-20%] right-[-10%] w-40 h-40 bg-white/20 dark:bg-white/5 rounded-full blur-3xl" />
      
      <div className="relative z-10">
        <div className={`flex items-center justify-between ${isCoachCollapsed ? '' : 'mb-6'}`}>
          <div className="flex items-center gap-2">
            <div className={`p-2 rounded-xl bg-white dark:bg-slate-800 shadow-sm ${current.color}`}>
              <SafeIcon icon="⚡" className="w-4 h-4" />
            </div>
            <span className={`text-[10px] font-black uppercase tracking-widest ${current.color}`}>
              AI Salgscoach
            </span>
          </div>
          <div className="flex items-center gap-3">
            {!isCoachCollapsed && (
              <div className="flex gap-1">
                {insights.map((_, idx) => (
                  <button 
                    key={idx} 
                    type="button"
                    onClick={() => setActiveInsight(idx)}
                    className={`w-1.5 h-1.5 rounded-full transition-all ${idx === activeInsight ? 'w-4 bg-slate-900 dark:bg-white' : 'bg-slate-300 dark:bg-slate-700'}`}
                    aria-label={`Vis coach-anbefaling ${idx + 1}`}
                  />
                ))}
              </div>
            )}
            <button
              type="button"
              aria-label={isCoachCollapsed ? 'Vis AI Salgscoach' : 'Skjul AI Salgscoach'}
              onClick={handleCollapseToggle}
              className="flex h-7 w-7 items-center justify-center rounded-lg text-lg font-bold text-blue-600 hover:bg-white/70 focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              {isCoachCollapsed ? '+' : '−'}
            </button>
          </div>
        </div>

        {!isCoachCollapsed && (
          <AnimatePresence mode="wait">
            <motion.div
              key={activeInsight}
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -20 }}
              className="space-y-4"
            >
              <div className="flex items-start gap-4">
                <div className={`w-12 h-12 rounded-2xl bg-white dark:bg-slate-800 flex items-center justify-center shadow-sm shrink-0 ${current.color} text-2xl`}>
                  <SafeIcon icon={current.icon} />
                </div>
                <div>
                  {current.deal ? (
                    <button
                      type="button"
                      onClick={handleOpenDeal}
                      className="text-left text-lg font-black text-slate-900 dark:text-white tracking-tight italic hover:text-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 rounded"
                    >
                      {current.title}
                    </button>
                  ) : (
                    <h3 className="text-lg font-black text-slate-900 dark:text-white tracking-tight italic">
                      {current.title}
                    </h3>
                  )}
                  <p className="text-sm text-slate-600 dark:text-slate-400 font-medium leading-relaxed mt-1">
                    {current.description}
                  </p>
                  {current.deal && (
                    <button
                      type="button"
                      onClick={handleOpenDeal}
                      className="mt-2 text-xs font-bold text-blue-700 hover:text-blue-900 hover:underline focus:outline-none focus:ring-2 focus:ring-blue-500 rounded"
                    >
                      Åpne lead
                    </button>
                  )}
                </div>
              </div>

              <div className="flex items-center justify-between pt-4">
                <button
                  type="button"
                  onClick={handleFollowUp}
                  className="flex items-center gap-2 text-[10px] font-black uppercase tracking-widest text-slate-900 dark:text-slate-200 group hover:text-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 rounded"
                >
                  {current.action} <SafeIcon icon="➡️" className="w-3.5 h-3.5 group-hover:translate-x-1 transition-transform" />
                </button>
                <div className="flex items-center gap-1 text-[8px] font-black text-slate-400 uppercase tracking-widest">
                  <SafeIcon icon="✅" className="w-3 h-3 text-emerald-500" /> Sanntidsanalyse
                </div>
              </div>

              {actionMessage && (
                <p
                  role="status"
                  className="rounded-xl border border-blue-200 bg-blue-50 px-3 py-2 text-xs font-semibold text-blue-700"
                >
                  {actionMessage}
                </p>
              )}
            </motion.div>
          </AnimatePresence>
        )}
      </div>
    </div>
  );
};

export default AICopilotWidget;